import * as React from "react";
import { MenuButton } from "./MenuButton";
import { SearchButton } from "./SearchButton";
import { SiteMenu } from "./SiteMenu";
import { Search } from "../Search/Search";
import { VelocityComponent } from "velocity-react";
import { ISelectListItem } from "./ISelectListItem";
import { MenuItem } from "../MobileMenu/MobileMenu.types";

interface ISiteHeaderProps {
  minorityLanguage: string;
  easyread?: string;
  signlanguage?: string;
  translatedStartpage?: string;
  translatedstartpageText?: string;
  menuText: string;
  searchText: string;
  isStartPage: boolean;
  searchPage: string;
  findUrl: string;
  logotype: string;
  currentPageId: number;
  treeNodes: MenuItem;
  siteMapUrl: string;
  minorityLanguages: ISelectListItem[];
  translatedItems: ISelectListItem[];
  translatedStartPageItems: ISelectListItem[];
  startPageid: number;
  signlanguageText: string;
  easyreadText: string;
  language: string;
  isCampaignPage?: boolean;
  highlightCurrentPage?: boolean;
  searchPlaceHolderText?: string;
  hideHeaderOnStartPage?: boolean;
  logoText: string;
  useSimpleMobileMenu?: boolean;
  currentPageAncestorsIds?: number[];
  logoWidth?: number;
  ariaLabelBackInMenu?: string;
  useSimpleMobileFullNode?: boolean;
}

interface ISiteHeaderState {
  menuIsOpen: boolean;
  searchIsOpen: boolean;
  searchIsReadyToFocus: boolean;
}

const searchPanelId = "search-panel";
const menuId = "mobile-menu-container";

/**
 * Renders the entire site menu: Hamburger button, search button
 */
export class SiteHeader extends React.Component<
  ISiteHeaderProps,
  ISiteHeaderState
> {
  private _searchPanelRef: HTMLElement;
  private _searchPanelHeight: string;

  constructor(props: ISiteHeaderProps) {
    super(props);
    this.state = {
      menuIsOpen: false,
      searchIsOpen: false,
      searchIsReadyToFocus: false,
    };
    this._searchPanelHeight = "8.1rem";
  }

  static defaultProps = {
    isCampaignPage: false,
    hideHeaderOnStartPage: false,
    logoWidth: 190,
  };

  setMenuOpenState = (): void => {
    if (!this.state.menuIsOpen) {
      document
        .getElementsByTagName("body")[0]
        .classList.add("lock-scrolling", "mobile-menu-open");
    } else {
      document
        .getElementsByTagName("body")[0]
        .classList.remove("lock-scrolling", "mobile-menu-open");
    }
    this.setState((prevState) => ({
      menuIsOpen: !prevState.menuIsOpen,
    }));
  };

  setSearchOpenState = (): void => {
    this.setState((prevState) => ({
      searchIsOpen: !prevState.searchIsOpen,
    }));
  };

  searchPanelAnimationBegin = (): void => {
    if (this.state.searchIsOpen) {
      this._searchPanelRef.style.display = "block";
      this.setState({
        searchIsReadyToFocus: true,
      });
    }
  };

  searchPanelAnimationComplete = (): void => {
    if (this.state.searchIsOpen) {
      this._searchPanelRef.style.display = "block";
    } else {
      this._searchPanelRef.style.display = "none";
    }
    this.setState({
      searchIsReadyToFocus: false,
    });
  };

  ariaLabelCloseandOpen = (): string => {
    var value = "";
    if (this.state.menuIsOpen) {
      value =
        this.props.language == "en" ? "Close site menu" : "Stäng sidmenyn";
    } else {
      value = this.props.language == "en" ? "Open site menu" : "Öppna sidmenyn";
    }
    return value;
  };

  render() {
    return (
      <header
        className={`siteheader  ${
          this.state.menuIsOpen ? "siteheader__fixed" : ""
        }`}
        aria-label={
          this.props.language == "en"
            ? "Site header (for small devices)"
            : "Sidhuvud (för små enheter)"
        }
      >
        <noscript>
          <div className="javascript-warning">
            Webbplatsen fungerar inte korrekt om du har stängt av funktionen för
            JavaScript.
          </div>
        </noscript>
        <div className="siteheader__top rtl">
          <MenuButton
            menuStateCallback={this.setMenuOpenState}
            menuText={this.props.menuText}
            menuIsOpen={this.state.menuIsOpen}
            menuId={menuId}
            ariaLabel={this.ariaLabelCloseandOpen()}
          />
          {this.props.logotype ? (
            <a href="/" className="siteheader__logo">
              <img
                src={this.props.logotype}
                alt={
                  this.props.logoText
                    ? this.props.logoText
                    : "Gå till startsidan"
                }
                width={this.props.logoWidth}
              />
            </a>
          ) : (
            <a href="/" className="mobile-nav-a mobile-nav-heading">
              {this.props.logoText}
            </a>
          )}
          <SearchButton
            searchStateCallback={this.setSearchOpenState}
            searchText={this.props.searchText}
            searchIsOpen={this.state.searchIsOpen}
            searchPanelId={searchPanelId}
          />
        </div>

        <VelocityComponent
          runOnMount={false}
          duration={250}
          animation={
            this.state.searchIsOpen
              ? // Open
                { height: this._searchPanelHeight }
              : // Close
                { height: 0 }
          }
          begin={this.searchPanelAnimationBegin}
          complete={this.searchPanelAnimationComplete}
        >
          <div
            id={searchPanelId}
            aria-hidden={!this.state.searchIsOpen}
            ref={(el) => (this._searchPanelRef = el)}
            className={"siteheader__searchpanel"}
          >
            <Search
              findUrl={this.props.findUrl}
              placeholder={
                this.props.searchPlaceHolderText
                  ? this.props.searchPlaceHolderText
                  : this.props.searchText
              }
              focus={this.state.searchIsReadyToFocus}
              searchPage={this.props.searchPage}
              searchId="search_mobile-siteheader"
            />
          </div>
        </VelocityComponent>

        <SiteMenu
          menuId={menuId}
          menuIsOpen={this.state.menuIsOpen}
          currentPageId={this.props.currentPageId}
          isStartPage={this.props.isStartPage}
          siteMapUrl={this.props.siteMapUrl}
          minorityLanguages={this.props.minorityLanguages}
          translatedItems={this.props.translatedItems}
          translatedStartPageItems={this.props.translatedStartPageItems}
          treeNodes={this.props.treeNodes}
          startPageid={this.props.startPageid}
          minorityLanguage={this.props.minorityLanguage}
          easyread={this.props.easyread}
          signlanguage={this.props.signlanguage}
          translatedStartpage={this.props.translatedStartpage}
          translatedstartpageText={this.props.translatedstartpageText}
          signlanguageText={this.props.signlanguageText}
          easyreadText={this.props.easyreadText}
          language={this.props.language}
          isCampaignPage={this.props.isCampaignPage}
          highlightCurrentPage={this.props.highlightCurrentPage}
          hideHeaderOnStartPage={this.props.hideHeaderOnStartPage}
          useSimpleMobileMenu={this.props.useSimpleMobileMenu}
          useSimpleMobileFullNode={this.props.useSimpleMobileFullNode}
          currentPageAncestorsIds={this.props.currentPageAncestorsIds}
        />
      </header>
    );
  }
}
